import { DOMAIN, SECURE, IS_LOCAL_DEVELOPMENT } from './cookies-config.js';
import { returnValueByEnv } from './environment.js';

/**
 * @typedef {object} LocalStorageKeys
 * @property {string} ADID - Key for storing ADID
 * @property {string} ANDROID_ID - Key for storing Android ID
 * @property {string} AOFL_TOKEN - Key for storing AOFL token
 * @property {string} APP_INSTANCE_ID - Key for storing App Instance ID
 * @property {string} APP_TOKEN - Key for storing Adjust app token
 * @property {string} DEVICE_CODE - Key for storing device code
 * @property {string} DEVICE_ID - Key for storing device ID
 * @property {string} EXPERIMENTS - Key for storing client experiments
 * @property {string} GPS_ADID - Key for storing GPS ADID
 * @property {string} IDFA - Key for storing IDFA
 * @property {string} IDFV - Key for storing IDFV
 * @property {string} SOURCE_INFO - Key for storing source information
 * @property {string} UDID - Key for storing UDID
 */
export const LOCAL_STORAGE_KEYS = {
	ADID: 'adid',
	ANDROID_ID: 'android_id',
	AOFL_TOKEN: 'aofl_token',
	APP_INSTANCE_ID: 'app_instance_id',
	APP_TOKEN: 'app_token',
	DEVICE_CODE: 'device_code',
	DEVICE_ID: 'device_id',
	// The device ID is different from the above. This is added to debug the storage mechanism of safari.
	DEVICE_ID_BACKUP: 'device_id_backup',
	EXPERIMENTS: 'client-experiments',
	GPS_ADID: 'gps_adid',
	IDFA: 'idfa',
	IDFV: 'idfv',
	SOURCE_INFO: 'source_info',
	UDID: 'udid',
};

/** localStorage key used to notify other windows/tabs that magic link was redeemed (storage event works across windows) */
export const MAGIC_LINK_REDEEMED_STORAGE_KEY = 'magic-link-redeemed';

/** sessionStorage key for passing the new-account auth token from magic-link-redeem → account-selection without exposing it in the URL */
export const MAGIC_LINK_NEW_USER_TOKEN_KEY = 'magic-link-new-user-token';

/**
 * Static identity for this web client. Carried by every emitted analytics
 * event so consumers can distinguish web-client-classic from other surfaces.
 * @readonly
 */
export const APP_INFO = {
	APP_NAME: 'abc-mkt-web',
	FRAMEWORK: 'sveltekit',
};

export const AOFL_PRODUCT_NAMES = {
	ABCMOUSE: 'abcmouse',
	ABCMOUSE_2_0: 'abcmouse_v2',
};

export const AOFL_LEGACY_ROLES = {
	abcmousev2_legacy_user: 'abcmousev2_legacy_user',
	abcmousev2_legacy_teacher: 'abcmousev2_legacy_teacher',
	abcmousev2_legacy_assessment: 'abcmousev2_legacy_assessment',
};

export const AOFL_2_0_ROLES = {
	abcmousev2_user: 'abcmousev2_user',
	abcmousev2_assessment_user: 'abcmousev2_assessment_user',
	abcmousev2_user_max_tier: 'abcmousev2_user_max_tier',
};

export const EXPERIMENT_ARMS = {
	ABCMOUSE_2_0: '10004',
	ABCMOUSE_1_0: '10003',
};

export const UNITY_VERSION_REQUIREMENTS = {
	MIN_VERSION_FOR_CONTINUE_TO_1_0: '8.71.0',
};

export const EVENT_NAMES = {
	CLIENT_VALIDATION: 'marketing.client_form_validation', // MIGRATED: remove after rollout (use EVENT_TYPES.FORM_SUBMISSION)
	DEBUG: 'marketing.client_debug',
	CAMPAIGN_CLICK: 'marketing.campaign_click', // MIGRATED: remove after rollout (use EVENT_TYPES.CLIENT_CAMPAIGN_CLICK)
	LINK_CLICK: 'marketing.page_link_click', // MIGRATED: remove after rollout (use EVENT_TYPES.CLIENT_PAGE_LINK_CLICK)
	PAGE_LOAD: 'marketing.page_load', // MIGRATED: remove after rollout (use EVENT_TYPES.CLIENT_PAGE_LOAD)
	PIXEL_FIRE: 'marketing.pixel_fire',
	VIDEO_PROGRESS: 'marketing.video_watched', // MIGRATED: remove after rollout (use EVENT_TYPES.VIDEO_PROGRESS)
	QR_CODE_VISITED: 'marketing.qr_code_visited', // MIGRATED: remove after rollout (use EVENT_TYPES.CLIENT_QR_CODE_VISIT)
	LINK_ENTRY_VISITED: 'marketing.link_entry_visited', // MIGRATED: remove after rollout (use EVENT_TYPES.CLIENT_LINK_ENTRY_VISIT)
	UI_SHOW: 'marketing.ui_show', // MIGRATED: remove after rollout (use EVENT_TYPES.UI_SHOW)
	ACCOUNT_SETTINGS_UPDATE: 'marketing.account_settings_update', // MIGRATED: remove after rollout (use EVENT_TYPES.ACCOUNT_SETTINGS_UPDATE)
};

export const MEDIA_CHANNEL = {
	AFFILIATE: 'affiliate',
	FACEBOOK: 'facebook',
	GOOGLE_DISPLAY: 'google_display',
	INCENTIVIZED: 'incentivized',
	INFLUENCERS: 'influencers',
	NATIVE_APP: 'native_app',
	OTHER_NON_PERFORMANCE: 'other_non_performance',
	OTT: 'ott',
	SEARCH_BRANDED: 'search_branded',
	SEARCH_NON_BRANDED: 'search_non_branded',
	THIRD_PARTY_EMAIL: 'third_party_email',
	UNKNOWN: 'unknown',
};

export const PAYMENT_TERM = {
	ANNUAL: 'annual',
	BIENNIAL: 'biennial',
	MONTHLY: 'monthly',
	SEMI_ANNUAL: 'semi_annual',
};

export const CLIENT_PLATFORMS = {
	ALT_PAYMENT_1_0: 'alt_payment_1_0',
	ALT_PAYMENT_2_0: 'alt_payment_2_0',
	ALT_PAYMENT_GOOGLE_1_0: 'alt_payment_google_1_0',
	ALT_PAYMENT_GOOGLE_2_0: 'alt_payment_google_2_0',
	IN_APP: 'in_app',
	DESKTOP_BROWSER: 'desktop_browser',
	MOBILE_BROWSER: 'mobile_browser',
};

// TODO: look to consolidtae with CLIENT_PLATFORMS
export const PLATFORMS = {
	DEFAULT: 'default_platform',
};

export const AOFL_PRODUCT_UUIDS = {
	ABCMOUSE: '2d6e4e3e-3711-499b-93e3-1919aac44a27',
	ABCMOUSE_2_0: 'b2892456-6e59-11ee-b962-0242ac120002',
	HOMESCHOOL: '4be90567-6d1a-43ab-afe6-51faa8d847eb',
	ABCMOUSE_ENGLISH_ES: 'c19722f3-9659-472e-9ccc-1aefa2c44803',
	ADVENTURE_ACADEMY: '4d984c26-1a72-11e7-93ae-92361f002671',
};

export const AOFL_USER_TYPES = {
	MIXED: 'mixed',
	CLASSIC: 'classic',
	ABCMOUSE_2_0: 'abcmouse_2_0',
};

export const GTM_EVENT_NAMES = {
	CONVERSION: 'conversion',
	PROSPECT_REG_SUBMITTED: 'prospect-reg',
	LOGIN: 'login',
};

export const PAYMENT_TYPES = {
	CREDIT_CARD: 'paymentech',
	PAYPAL: 'paypal',
	APPLE_PAY: 'apple_pay',
	MOCK_CREDIT_CARD: 'mock_cc_gateway',
};

export const ENVIRONMENTS = {
	DEVELOPMENT: 'development',
	STAGING: 'staging',
	PRODUCTION: 'production',
	LOCAL_DEVELOPMENT: 'local_development',
};

/**
 * Sentry error severity levels for use with captureException and captureMessage
 * @typedef {object} SentryErrorLevels
 * @property {string} FATAL - Highest severity level for critical errors
 * @property {string} ERROR - Standard error level (default for exceptions)
 * @property {string} WARNING - Warning level for recoverable issues
 * @property {string} LOG - Log level for informational messages
 * @property {string} INFO - Info level for general information
 * @property {string} DEBUG - Debug level for development debugging
 */
export const SENTRY_ERROR_LEVELS = {
	FATAL: 'fatal',
	ERROR: 'error',
	WARNING: 'warning',
	LOG: 'log',
	INFO: 'info',
	DEBUG: 'debug',
};

export const SUBSCRIPTION_TYPES = {
	NEW_SUBSCRIPTION: 'firstTimeSubscription',
	// covers both reactivation and buying additional products.
	EXISTING_SUBSCRIPTION: 'existingSubscription',
	ITEM_PURCHASE: 'itemPurchase',
};

export const TRANSACTION_TYPE = {
	PREPROCESS: 'preprocess',
	PROCESS: 'process',
};

export const APP_CALLS = {
	ADJUST_EVENT: 'event://adjustEvent',
	CLOSE_APP: 'application://close',
	GO_BACK: 'location://goBack?showParentGate=false',
	LOG_EVENT: 'event://logEvent',
	LOGIN: 'session://login',
	LOGOUT: 'application://logout',
	NAVIGATE: 'location://navigate',
	PURCHASE: 'transaction://purchase',
	RESTORE: 'transaction://restore',
	SESSION_GET: 'session://get',
	UPGRADE: 'transaction://upgrade',
};

export const APP_CALL_CALLBACKS = {
	ADJUST_EVENT: 'adjustCallback',
	FIREBASE_EVENT: 'firebaseCallback',
	LOGIN: 'loginCallback',
	PURCHASE: 'purchaseCallback',
	RESTORE: 'restoreCallback',
	UPGRADE: 'upgradeCallback',
};

export const THIRD_PARTY_EVENTS = {
	SUBSCRIBE: 'Subscribe',
	INITIATE_CHECKOUT: 'InitiateCheckout',
};

export const APP_STORES = {
	ITUNES: 'iTunes',
	GOOGLE_PLAY: 'GooglePlay',
	AMAZON: 'Amazon',
	APPLE_PAY_MERCHANT_ID: 'merchant.abcmouse.com',
};

export const UsernameTypes = {
	EMAIL: 'email',
};

export const OUTBOUND_LINKS = {
	get IPV6() {
		return returnValueByEnv({
			dev: 'https://deq4mt61w2hci.cloudfront.net/trk',
			production: 'https://ipv6.abcmouse.com/trk',
		});
	},
};

export const OUTBOUND_PATH = {
	LINK_TO_APP_1_0: 'https://xnvn.adj.st/?provider=adjust&adj_t=1rntjwqr&adj_label=token&token=',
	LINK_TO_APP_2_0: 'https://abcmouse.go.link/app/auth?adj_t=1kmlo5gl&adj_label=token&token=',
	LINK_TO_APP_1_0_AMAZON:
		'https://abcmouse-1-0-amz.go.link/?provider=adjust&adj_t=1td8cyt7&adj_label=token&token=',
	LINK_TO_APP_2_0_AMAZON:
		'https://abcmouse-amz.go.link/app/auth?adj_t=1r7kvsdr&adj_label=token&token=',
};

export const PAGES = {
	INTERNAL: {
		AA_OFFER: '/abc/aa-offer',
		APP_CHOICE: '/abc/app-choice',
		ASSESSMENT_PURCHASE: '/abc/assessment-purchase',
		CANCEL_SUBSCRIPTION: {
			ASSESSMENT_CENTER: '/abc/cancel-subscription/assessment-center',
			CANCEL_FEEDBACK: '/abc/cancel-subscription/cancel-feedback',
			CONFIRMATION: '/abc/cancel-subscription/confirmation',
			DEFAULT: '/abc/cancel-subscription',
			PAUSE: '/abc/cancel-subscription/pause',
			PAUSE_MANAGE: '/abc/cancel-subscription/pause/manage',
			PAUSE_RESUMED: '/abc/cancel-subscription/pause/resumed',
			PAUSE_SUCCESS: '/abc/cancel-subscription/pause/success',
			SAVE_OFFER: '/abc/cancel-subscription/save-offer',
			SUCCESS: '/abc/cancel-subscription/success',
		},
		CLAIMED_OFFER: '/abc/claimed-offer',
		DESKTOP_CHOICE: '/abc/desktop-choice',
		DOWNLOAD_APP: '/abc/download-app',
		FORGOT_PASSWORD: '/abc/forgot-password',
		GIVEAWAY: {
			FAMILY_VACATION_2026: '/abc/giveaway/family-vacation-2026',
		},
		GIVE_A_GIFT: '/abc/purchase-gift/',
		HOME: '/abc',
		HOME_AOFLJS: '/abc/?arm=9100',
		HOME_ES: '/abc/es/',
		INBOUND_GATEWAY: '/abc/inbound-gateway',
		INSIDE_PAYWALL: '/abc/inside-pw',
		LEGACY: {
			ABOUT_US: '/about-us',
			ABOUT_US_ADVISORY_BOARD: '/about-us#advisory_board',
			COOKIE_POLICY: '/cookie-policy',
			CUSTOMER_SUPPORT: '/customer-support',
			EDUCATIONAL_GAMES: '/educational-games',
			FAQS: '/faqs',
			GIFT: '/gift',
			GIFT_CARDS: '/code',
			HOMESCHOOL: '/homeschool',
			KINDERGARTEN: '/kindergarten',
			LIBRARIES: '/libraries',
			LOGIN: '/login',
			MATH: '/math',
			NEWSLETTER: '/newsletter',
			ONBOARDING_PAGE: '/html5/onboarding',
			PHONICS: '/phonics',
			PRESCHOOL: '/preschool',
			PRE_KINDERGARTEN: '/pre-kindergarten',
			PRIVACY_POLICY: '/privacy',
			READING: '/reading',
			RESULTS_AND_REFERENCES: '/results-and-references',
			TANDC: '/tandc',
			TEACHERS: '/teachers',
		},
		LINKS: {
			APP_CHOICE: '/abc/app-choice',
			QR_ENTRY: '/abc/qr-entry/',
			LINK_ENTRY: '/abc/link-entry/',
		},
		LOGIN: '/abc/login',
		LOGIN_ACCOUNT_SELECTION: '/abc/login-account-selection',
		LOGIN_EXPIRED_LINK: '/abc/login-expired-link',
		LOGIN_REDEEMED_LINK: '/abc/login-redeemed-link',
		LOGIN_SAME_DEVICE_SUCCESS: '/abc/login-same-device-success',
		LOGIN_SECONDARY_DEVICE_SUCCESS: '/abc/login-secondary-device-success',
		LOGIN_TOO_MANY_ATTEMPTS: '/abc/login/too-many-attempts',
		LOGOUT: '/abc/logout',
		MAGIC_LINK_PENDING: '/abc/magic-link-pending',
		OFFER_EXPIRED: '/abc/expired',
		PARENT_SECTION: {
			ASSESSMENT_CENTER: '/abc/parent-section/assessment-center',
			BILLING: '/abc/parent-section/membership/billing',
			DEFAULT: '/abc/parent-section',
			MEMBERSHIP: '/abc/parent-section/membership',
			PAYMENT: '/abc/parent-section/membership/payment',
		},
		PREMIUM: '/abc/premium',
		PRINTABLES: {
			DEFAULT: '/abc/printables',
		},
		PROSPECT_REGISTER: '/abc/prospect-register',
		REACTIVATION: '/abc/reactivation',
		REDEEM: '/abc/redeem',
		RESET_LINK_EXPIRED: '/abc/reset-link-expired',
		RESET_PASSWORD: '/abc/password-reset',
		RESET_PASSWORD_SUCCESS: '/abc/password-reset-success',
		STARTER_PACK: '/abc/starter-pack',
		CREATE_ACCOUNT: '/abc/create-account',
		SUBSCRIPTION: '/abc/subscription',
		SUBSCRIPTION_CONSUME: '/abc/subscription-consume',
		SUBSCRIPTION_OFFER: (/** @type {string} */ offer) => `/abc/subscription/offer/${offer}`,
		SUPPORT_CHATBOT: {
			DEFAULT: '/abc/support-chatbot',
		},
		TUTOR_OFFER: '/abc/tutor-offer',
		UNIVERSAL: {
			MAGIC_LINK_REDEEM: '/abc/universal/magic-link-redeem',
		},
		OAUTH_CALLBACK: '/abc/oauth/callback', // Route does not need to actually exist.
		UPGRADE: '/abc/upgrade',
	},
	EXTERNAL: {
		get ABC_SERVICE_LAYER_BASE_URL() {
			return returnValueByEnv({
				dev: 'https://dev.app.abcmouse.com',
				production: 'https://app.abcmouse.com',
				staging: 'https://app.abcmouse.com',
			});
		},
		ABCMOUSE: 'https://www.abcmouse.com',
		ABCMOUSE_1_0_APP_DEEP_LINK: 'aofl.abcmouse.com://',
		get ABCMOUSE_2_0() {
			return returnValueByEnv({
				dev: 'https://dev.app.abcmouse.com/app',
				production: 'https://app.abcmouse.com/app',
				staging: 'https://stg.app.abcmouse.com/app',
			});
		},
		ABCMOUSE_2_0_APP_HOME_DEEP_LINK_: 'abcmouseapp://app.abcmouse.com/app/feature?path=/',
		ABCMOUSE_2_0_WEB_APP_URL: 'https://app.abcmouse.com/app/',
		ABCMOUSE_SEO_GAMES_PAGE: 'https://www.abcmouse.com/learn/games',
		ACCESSIBILITY: 'https://www.ageoflearning.com/accessibility',
		ACCESSIBILITY_STATEMENT: 'https://www.ageoflearning.com/accessibility/',
		ACTIVITIES: 'https://www.abcmouse.com/learn/category-activities',
		ADVENTURE_ACADEMY: 'https://www.adventureacademy.com/',
		ALLABOUT_DNT: 'http://www.allaboutdnt.org',
		AMAZON_APP_STORE: 'http://www.amazon.com/gp/mas/dl/android?asin=B0F77MSX3T',
		AOFL: 'https://www.ageoflearning.com',
		AOFL_ABCMOUSE_COOKIE_POLICY: 'https://www.ageoflearning.com/abc-cookie-policy/',
		AOFL_BRAND_ABCM: 'https://www.ageoflearning.com/brand/abcmouse_landing_v1',
		AOFL_DEVICES: 'https://www.ageoflearning.com/devices/aofl_devices_en.pdf',
		AOFL_FOR_SCHOOLS: 'https://www.ageoflearning.com/schools/',
		AOFL_RESEARCH: 'https://www.ageoflearning.com/research/',
		CANCELLATION_SURVEY_2_0: 'https://ageoflearning.sjc1.qualtrics.com/jfe/form/SV_0x2vngZjCVRZ0BU',
		CANCELLATION_SURVEY_1_0: 'https://ageoflearning.sjc1.qualtrics.com/jfe/form/SV_9zYGYCR6SuhI4iW',
		AOFL_ADDRESS:
			'https://www.google.com/maps/place/101+N+Brand+Blvd+7th+Floor,+Glendale,+CA+91203/@34.1464212,-118.2580452,17z/data=!3m1!4b1!4m5!3m4!1s0x80c2c0ff804e9233:0x663dc9c9ed0af38e!8m2!3d34.1464212!4d-118.2554703?entry=ttu&g_ep=EgoyMDI2MDMyNC4wIKXMDSoASAFQAw%3D%3D',
		CAREERS: 'https://www.ageoflearning.com/careers/',
		CCPA_METRICS: 'https://www.ageoflearning.com/ccpa/',
		CUSTOMER_SUPPORT: 'https://support.abcmouse.com',
		CUSTOMER_SUPPORT_CONTACT_US: 'https://support.abcmouse.com/hc/en-us/articles/360047572854',
		CUSTOMER_SUPPORT_FAQS:
			'https://support.abcmouse.com/hc/en-us/articles/360053552753-What-devices-should-I-use-with-ABCmouse-',
		CUSTOMER_SUPPORT_FORGOT_EMAIL:
			'https://support.abcmouse.com/hc/en-us/articles/4413252218903-Account-Access',
		CUSTOMER_SUPPORT_FORGOT_PASSWORD:
			'https://support.abcmouse.com/hc/en-us/articles/4413252218903?forgotpassword=true',
		CUSTOMER_SUPPORT_FORGOT_PASSWORD_PLUS:
			'https://support.plus.abcmouse.com/hc/en-us/articles/19158148938263-Account-Access',
		CUSTOMER_SUPPORT_PLUS: 'https://support.plus.abcmouse.com',
		CUSTOMER_SUPPORT_PLUS_CONTACT_US: 'https://support.abcmouse.com/hc/en-us/p/contact',
		FACEBOOK: 'https://www.abcmouse.com/outsideurl?url=facebook',
		FACEBOOK_GROUP: 'https://www.facebook.com/groups/abcmouse.orientation/about/',
		FACEBOOK_PAGE: 'https://www.facebook.com/ABCmouse/',
		GALAXY_APP_STORE: 'https://galaxystore.samsung.com/detail/com.aofl.abcmouse',
		GOOGLE_APP_STORE: 'https://play.google.com/store/apps/details?id=com.aofl.abcmouse',
		GOOGLE_UCB_LEARN_MORE: 'https://support.google.com/googleplay/answer/11174377',
		HOMESCHOOLPLUS: 'https://www.homeschoolplus.com',
		HOMESCHOOLPLUS_SUBSCRIPTION: 'https://www.homeschoolplus.com/subscription/',
		INSTAGRAM: 'https://www.instagram.com/abcmouse/',
		IOS_APP_STORE: 'https://apps.apple.com/us/app/abcmouse/id6460300848',
		IOS_APP_STORE_KIDS_LEARNING_GAMES:
			'https://apps.apple.com/us/app/abcmouse-kids-learning-games/id6460300848',
		IPV6: 'https://ipv6.abcmouse.com/trk',
		KID_SAFE: 'https://www.kidsafeseal.com/certifiedproducts/abcmouse.html',
		LEARN: 'https://www.abcmouse.com/learn',
		MEDIA: 'https://www.ageoflearning.com/media/',
		PINTEREST: 'https://www.abcmouse.com/outsideurl?url=pinterest',
		PINTEREST_PAGE: 'https://www.pinterest.com/abcmouse/',
		PRINTABLES: 'https://www.abcmouse.com/learn/category-printables-worksheets',
		PRIVACY_DE: 'https://www.ageoflearning.com/abc-privacy-german',
		PRIVACY_ES: 'https://www.ageoflearning.com/abc-privacy-current-es/',
		PRIVACY_HUB: 'https://www.ageoflearning.com/privacy-policies/',
		PRIVACY_PDF: 'https://img.abcmouse.com/pdf/privacy.pdf',
		NMHP_PRIVACY_POLICY: 'https://www.abcmouse.com/learn/abcmouse-privacy-policy',
		PRIVACY_POLICY: 'https://www.ageoflearning.com/abc-privacy-current/',
		PRIVACY_PORTAL: 'https://my.datasubject.com/16BSaVUFbILW04pyR/38184',
		PRIVACY_SE: 'https://www.ageoflearning.com/abc-privacy-swedish',
		SHOP_FAQ: 'https://shop.abcmouse.com/pages/faq/?src_tag=shop_abcm',
		SHOP_ORDER_STATUS: 'https://shop-account.abcmouse.com/orders?locale=en&region_country=US',
		SHOP_PRIVACY_POLICY: 'https://shop.abcmouse.com/pages/privacy-policy/?src_tag=shop_abcm',
		SHOP_RETURN_AND_REFUNDS: 'https://shop.abcmouse.com/policies/refund-policy',
		SHOP_SHIPPING_POLICY: 'https://shop.abcmouse.com/policies/shipping-policy',
		SHOP_TANDC: 'https://shop.abcmouse.com/policies/terms-of-service/?src_tag=shop_abcm',
		SUNMAID_COLORING_PAGES_PDF: 'https://img.abcmouse.com/pdf/sunmaid_alphabet_coloring_pages.pdf',
		SUNMAID_LETTER_TRACING_PDF: 'https://img.abcmouse.com/pdf/abcmouse_letter_tracing.pdf',
		SUNMAID_MATH_WITH_RAISINS_PDF: 'https://img.abcmouse.com/pdf/sunmaid_book_letter_12.pdf',
		SUNMAID_SIGHT_WORDS_PDF: 'https://img.abcmouse.com/pdf/abcmouse_sunmaid_sight_words_rgb_06.pdf',
		SUNMAID_WALMART: 'https://www.walmart.com/cp/sunmaidbacktoschool/3335011',
		TANDC_DE: 'https://www.ageoflearning.com/abc-tandc-german',
		TANDC_SE: 'https://www.ageoflearning.com/abc-tandc-swedish',
		TEACHERS: 'https://www.abcmouse.com/teachers',
		NMHP_TERMS_CONDITIONS: 'https://www.abcmouse.com/learn/abcmouse-terms-conditions',
		TERMS_CONDITIONS: 'https://www.ageoflearning.com/abc-t-and-c-current/',
		TERMS_CONDITIONS_ES: 'https://www.ageoflearning.com/abc-tandc-current-es/',
		TIKTOK: 'https://www.tiktok.com/@abcmouse',
		YOUTUBE: 'https://www.youtube.com/@ABCmouse',
		ABOUT_US: 'https://www.abcmouse.com/learn/about-abcmouse',
		PRESS: 'https://www.abcmouse.com/learn/press-releases',
		LIBRARIES: 'https://www.abcmouse.com/blho-check-out',
		SHOP: 'https://shop.abcmouse.com/',
	},
};

export const QUERY_PARAM_KEYS = {
	ABCM_TOKEN: 'abcm-token',
	ADID: 'adid',
	ALREADY_SUBSCRIBED: 'alreadySubscribed',
	ANDROID_ID: 'android_id',
	APP_INSTANCE_ID: 'app_instance_id',
	APP_STORE_NAME: 'store',
	APP_TOKEN: 'app_token',
	APP_VERSION: 'app_version',
	BRAZE_TOKEN: 'braze_token',
	CJ_EVENT: 'cjevent',
	CLIENT_TYPE: 'ct',
	DEEP_LINK: 'deepLink',
	DEVICE_ID: 'device_id',
	DISTRIBUTION_PLATFORM: 'distribution_platform',
	DST: 'dst',
	EMAIL_CLICK: '_0c83',
	ERROR_TYPE: 'error_type',
	EXPIRES_TIMESTAMP: 'expires_timestamp',
	EXTRA_PATH_DATA: 'extra_path_data',
	GOTO: 'to',
	GPS_ADID: 'gps_adid',
	IDFA: 'idfa',
	IDFV: 'idfv',
	LINK_FROM_APP_PLATFORM: 'linkFromAppPlatform',
	OPEN_SUPPORT_CHATBOT: 'open_support_chatbot',
	PAUSE_FOR: 'pause_for',
	PAYLOAD: 'payload',
	PRODUCT_GROUP: 'product_group',
	PRODUCT_HASH: 'productHash',
	PRODUCT_HASH_ANNUAL: 'productHashAnnual',
	PRODUCT_HASH_MONTHLY: 'productHashMonthly',
	PRODUCT_TYPE: 'b_term',
	PRODUCT_UUID: 'product_uuid',
	REFERRER: 'referrer',
	SOURCE: 'source',
	SOURCE_TAG: 'src_tag',
	STORE_TYPE: 'storeType',
	SUBSCRIPTION_CATEGORY: 'subscription_category',
	SUBSCRIPTION_UUID: 'subscription_uuid',
	SUPPORT_CHATBOT_FULLSCREEN: 'support_chatbot_fullscreen',
	SUPPORT_CHATBOT_VISIBLE: 'support_chatbot_visible',
	TARGET_AOFL_PRODUCT_UUID: '_ee4c',
	TOKEN: 'token',
	TV_DEVICE_CODE: 'tvdc',
	TYPE: 'type',
	UDID: 'udid',
	UNIQUE_OFFER_EXPIRATION_TIMESTAMP: 'ts',
	USER_TOKEN: 'token',
	VIEWER_COUNTRY: 'viewerCountry',
	VIEWER_COUNTRY_REGION: 'viewerCountryRegion',
};

export const QUERY_PARAM_VALUES = {
	B_TERM: {
		MONTHYL: 'monthly',
		ANNUAL: 'annual',
	},
};

export const CAMPAIGN_PIXEL_TYPES = {
	CONVERSION: 'conversion',
	LANDING: 'landing',
	PROSPECT_REGISTER: 'sign-up',
};

export const REG_PATH_KEYS = {
	DEFAULT_SUBSCRIBER: 'defaultSubscriber',
	DEFAULT_RESUBSCRIBER: 'defaultResubscriber',
	NO_AA_SUBSCRIBER: 'noAASubscriber',
	IN_APP_SUBSCRIBER: 'inAppSubscriber',
	IN_APP_RESUBSCRIBE: 'inAppResubscriber',
	GOOGLE_IN_APP_SUBSCRIBER: 'googleInAppSubscriber',
	IN_APP_UCB_SUBSCRIBER: 'inAppUcbSubscriber',
	IN_APP_UCB_RESUBSCRIBER: 'inAppUcbResubscriber',
	SUBSCRIBER_NO_PATH: 'subscriberNoPath',
	RESUBSCRIBER_NO_PATH: 'resubscriberNoPath',
	IN_APP_SAMSUNG_SUBSCRIBER: 'inAppSamsungSubscriber',
	IN_APP_SAMSUNG_RESUBSCRIBER: 'inAppSamsungResubscriber',
	DEFAULT_IN_APP_CONSUMPTION_2_0: 'defaultInAppConsumption',
	DEFAULT_IN_APP_CONSUMPTION_RESUBSCRIBE_2_0: 'defaultInAppConsumptionResubscribe',
	IOS_ALT_PAYMENT_SUBSCRIBER_1_0: 'iosAltPaymentSubscriber1_0',
	IOS_ALT_PAYMENT_SUBSCRIBER_2_0: 'iosAltPaymentSubscriber2_0',
	IOS_ALT_PAYMENT_RESUBSCRIBER_1_0: 'iosAltPaymentResubscriber1_0',
	IOS_ALT_PAYMENT_RESUBSCRIBER_2_0: 'iosAltPaymentResubscriber2_0',
	ANDROID_ALT_PAYMENT_SUBSCRIBER_1_0: 'androidAltPaymentSubscriber1_0',
	ANDROID_ALT_PAYMENT_RESUBSCRIBER_1_0: 'androidAltPaymentResubscriber1_0',
	ANDROID_ALT_PAYMENT_SUBSCRIBER_2_0: 'androidAltPaymentSubscriber',
	ANDROID_ALT_PAYMENT_RESUBSCRIBER_2_0: 'androidAltPaymentResubscriber2_0',
	SPECIAL_OFFER_SUBSCRIBER: 'specialOfferSubscriber',
	SPECIAL_OFFER_SUBSCRIBER_NO_SEMI_ANNUAL: 'specialOfferSubscriberNoSemiAnnual',
};

/**
 * Registration paths where the semi-annual "No, thanks" upsell modals are skipped on upgrade and assessment.
 */
export const SUPPRESS_SEMI_ANNUAL_SUBSCRIPTION_PATHS = [
	REG_PATH_KEYS.SPECIAL_OFFER_SUBSCRIBER_NO_SEMI_ANNUAL,
];

export const REG_PATH_STEPS = {
	ASSESSMENT: 'REGPATH_ASSESSMENT',
	TUTOR_OFFER: 'REGPATH_TUTOR_OFFER',
	DOWNLOAD_APP: 'REGPATH_DOWNLOAD_APP',
	AA_OFFER: 'REGPATH_AA_OFFER',
	STARTER_PACK: 'REGPATH_STARTER_PACK',
	COMPLETE: 'REGPATH_COMPLETE',
	SUBSCRIBED: 'REGPATH_SUBSCRIBED', // Step at which the user has subscribed.
	UPGRADE: 'REGPATH_UPGRADE',
	RESET: 'DEFAULT_STEP', // Step to reset a reg-path: should not be included in any reg-path, used primarily for reactivation users.
};

/**
 * Marker hash used for "No thanks" option in upgrade flows.
 * This is not a real subscription hash - it's a UI convention.
 */
export const NO_THANKS_HASH = 'no_thanks_hash';

export const SUBSCRIPTION_HASHES = {
	PAYMENT_TECH: {
		aa_offer: '78a35b75-997e-44d6-8f2d-7bb6f677d164',
		monthly_fwf: '751b26740991c3895e4d2069beedfd28', // $12.99
		monthly_fwf_1499: 'cf643269fa6bf53718640211a2c582f64e996fc696cbc994df518cd2a4b89e6b', // $14.99
		monthly_fmf: 'd4vdn3idd9kr5uoexm4xij4zhhlcc9hc', // $14.99
		annual: 'a705135a5ca0263he12632d074f7796c',
		semi_annual: 'e6d014c1363cae08b8c019b7cf5d935a',
		upgrade: '6d24d3e822431efca22d73f50d4da9fb', // $45.00
		assessment_monthly: '30a5c22b794f10a376ae452bf78fb4f2', // $3.95
		assessment_annual: '2530146ebb330f58f5d633b2b23c868c',
		assessment_semi_annual: 'b0e51d17de8e6f364e414f11719f9b4c',
		gift_one_year: 'dbee304be9df12b57fd6279c76dab1eb',
		gift_two_year: '3f141af954cb78d2387f34f3be357f95',
		monthly_reactivation: 'e91fba0ff4be204eb6c6ff64d5da9057', // $14.99
		annual_reactivation: 'cb19da4514dgb2dbc5419e18s2c72b7c', // $79.99
		annual_reactivation_upgrade: 'cd44fasa2e3e754114d36bf572d4e6c2', // $59.40
		semi_annual_reactivation: '7a4852696c02f5bf2f72b93badce0f75', // $29.99
		monthly_fwf_2_0: 'ce2f536ce33f40f5890f05b924f00047b7daf7494bea8472c7c921222f5a37da', // $14.99 - 2_0
		monthly_fmf_2_0: 'f1eab9c7f585ee6e6b45e993af095abc96cbe361dc575f5539e3aaad0bc0a6a6', // $14.99 - 2_0
		monthly_fmf_2_0_upgrade_offer:
			'165fb9e5fdddb0a3eb65d2757bf88ad2f56a9804cec514670259c3327d071965', // $14.99 - 2_0 - Upgrade Offer
		monthly_no_trial_2_0: '872652378a49578c6a4d5c2d1b4eb959b7fe04cbdf0a87a30c02d184bedca8b7', // $12.99 - 2_0
		annual_2_0: '0276c52238259b3948b86616639ffaf3fa9be4eb89c976085b98835648eb9882', // $45.00 - 2_0
		annual_2_0_ios_alt_payment_1_0:
			'50c94388e3808b58b699670771a8f70835ba30512038a0b691f6a21a170c72d7', // $45 First Year ($59.99 Annual) Product ID: 1844
		annual_2_0_upgrade_offer: '54847633663053095dce1cc409214c099cb956dc1cf3672b1471e7b938de954f', // $39.99 - 2_0 - Upgrade Offer
		semi_annual_2_0: 'a671ba9535d019ed4ae92f2974fe665bd1fd9f06e08f67aadaf9ccff719e46ac', // $25.00 - 2_0
		monthly_reactivation_2_0: 'da4c2564d555985bb50aa3b631f4839f2ffe43a4096e069818ee9f4d89c0c20b', // $14.99 - 2_0 Product ID: 1888
		annual_reactivation_2_0: '5e9550df7b0347c3cc67470062b4546f12bbf5cd58fe3e0d5fd93b86dae854b3', // $79.99 - 2_0 Product ID: 1885
		semi_annual_reactivation_2_0:
			'7e5a044c75ee4a36d86fb93736e261c0936f1d193357ea799e26e09355d4686e', // $29.99 - 2_0 Product ID: 1887
		annual_reactivation_upgrade_2_0:
			'c9e4c363eae1fb5954ec2ab17b0d9584df5ffe77531c9e0b8f0708b115dc4886', // $59.40 - 2_0 Product ID: 1886
		assessment_monthly_2_0: '8b48642b0d6244f6650daf8d6bf26776397d11ec3e76a7587f4407bff738cacc', // $3.99 - 2_0 FMF
		assessment_monthly_2_0_499: '87661d063052f9c9f9d17c7a9e3498ab2c802a007d0ec42a556a5a12357ece84', // $4.99 - 2_0 FMF
		assessment_monthly_2_0_799: 'bbb731f11382b55b52fe36648f8c053f2199027bd26bad31aa9b2af48aed12b8', // $7.99 - 2_0 FMF
		assessment_monthly_2_0_999: 'fcc56a88a276564578dc1045dfeb36a630b5cf02088a04d50fbfc7056fd87222', // $9.99 - 2_0 FMF
		assessment_annual_2_0: '3f9a5f7b6ce8e1d2dbe1cdcf2f2c5b8fb206fe845e9b48daad0891fee7ea1bb5', // 39.99 - 2_0
		assessment_semi_annual_2_0: '606200ee609d5e9114c7b43f99f986deabf6d624a812fdcc36c0b4a57ea73137', // 19.99 - 2_0
		offer_annual_2_0: 'b07d926a57e60328660d47450a69ccbc6df2589a1d1396fa0fce6dcd036c720a', // 29.99 - 2_0
		offer_annual_1_0: 'c72e89a09e355cb868b9d0a7ae029790', // 29.99 - 1_0
		offer_annual_max_tier: '787f978786fda10892a30198c91ef566bf7cc225550ed2f556b85b535427fe6b', // 79.99 - Max Tier Offer
		offer_annual_max_tier_2_0: '4cc37905949c4d2a430de4e90be6a463020da6d577e6e29cbbde2cf703c2c35b', // 99.99 - 2_0 - Max Tier Offer
		starter_pack_offer_one: '08b767f83d1c812733f72ec36d6d8c8d0c2a4dfab8814a8b8ebdd18e49b1da39', // $24.99
		starter_pack_offer_two: '2abad8134c7723f55fcaf8477d41e6813cf2b6fa1eba1811c47af965ee48e413', // $39.99
		starter_pack_offer_three: 'cf8bbbffbee0141909afab0ac86608c29b6681d7702e7a34d1e2ab19b4c57785', // $54.99
		single_credit_tutor_offer: 'cd6010cff27adbba334e5e2319f6c3c19dbef538a2402d832e0ee5f18d55ddd0', // $25.00 - Single Credit Tutor Offer
		annual_2_0_direct_price_a: '69d33a5d56bbf5a78e1904e9cb4368971b8a7ba63f049aaca346bc9c3f1c305b', // $59.99 (Yearly) - No Trial - Tax - 1891
		annual_2_0_upgrade_offer_direct_price_a:
			'4fac36b4fab5d30c4f9ea83a17f8429479da09162a7abfd0a3435d3f9ce6fc3f', // $49.99 (Yearly) - 1914
		semi_annual_2_0_direct_price_a:
			'e0d9990588d8978ac2b5ccfe09ef6506887137eda487a51ca928a7051c018232', // $30.00 - 1911
		annual_2_0_direct_price_b: '75161e248909be7ba57ccfb1ee84389182127495b933a963295fed8b400cc7ee', // $69.99 (Yearly) - No Trial - Tax - 1892
		semi_annual_2_0_direct_price_b:
			'675d2036741af48837761b585258859d6017399b5c3465af5b8d3368cc8b8c78', // $35.00 - 1912
		annual_2_0_direct_price_c: '926f33e762a9af4e4c01b5325d320416a158d173e2e8e3c3a0c602f3b99ffeba', // $79.99 (Yearly) - No Trial - Tax - 1893
		semi_annual_2_0_direct_price_c:
			'66bc98c52a1e411c1cb4acda9cf677e962ffb4ddc5cd4a0145ad0ba233e71597', // $40.00 - 1913
		annual_2_0_upgrade_offer_direct_price_b_c:
			'f9e907df2d70db310b8883e9b5b04b8d900306fe0ee8b4b88ffb2d28f8c3340f', // $59.99 (Yearly) - 1915
	},
	IOS: {
		monthly_reactivation: '9e0c85b3e45f689aa3a314b095d3b5e2', // $14.99 (No. FWF)
		annual_reactivation: '8a4e4472f86b2b934b4e629bf774b39c', // $59.99
		monthly: '9e19861656864e08d9167db751086ec0', // product id: 2732 - $14.99 First Month Free
		annual: 'ad2950d952c358c62dcf9b9c001ccdee', // product id: 2736 - $45 First Year ($59.99 Annual)
		semi_annual: '0c6802bb28d2e18329776d8a316f1775', // product id: 2734 - $39.99
		upgrade: 'e09e020dde2b2460e3185d702a11b244', // product id: 2735 - $49.99
		assessment: 'c9a609d125f991492ee007cc357ff2b9', // $39.99 (Annual)
	},
	IOS_MKTABC_3691_ALT_PAYMENT_TEST: {
		monthly_reactivation: '10898e5b1eeedfd0da0ef2c10de45739', // product id: 2741 - $14.99 no trial
		annual_reactivation: 'b921a20c54bd95b00c1037b11a6d1cf7', // product id: 2746 $59.99 upfront annual
		monthly: 'e4e9d671225eeac51ff5784f4f21ce7b', // product id: 2745 - $14.99 First Month Free
		annual: 'a63a594898fca13efc89ee7640597aca', // product id: 2744 - $45 First Year ($59.99 Annual)
		semi_annual: '32ea168aecaf02dd223af15a78fa5b7d', // product id: 1742 - $39.99
		upgrade: 'ff89a9fe4e13691998003024dc00f7b9', // product id: 2743 - $49.99
		assessment: 'aa047073b571692520c230d25ecf9a09', // product id: 2748 $39.99 (Annual)
	},
	AMAZON: {
		monthly_reactivation: 'cbe475080673df42c6bdfb995b46931e', // $12.99
		annual_reactivation: 'a1430b7a89569a3d3270160cb882d995', // $59.99
		annual: '7d8a6b9c4e3f1b5d2c7e0f1a8b2d6c9f', // $49.99
		semi_annual: '3c51e00d5db8dabba4bd3f7a5d45e6cd', // $29.99
		monthly: 'bdc8247b33e3369a502e2ec85fc46312', // $14.99
	},
	ANDROID: {
		annual_iap: 'c59d36a81c3f20ff504400d872b93036', // $45 annual
		annual_resub_iap: 'dbca284350428313df5993a53a69dbfb', // $45 reactivation
		monthly_iap: '00e50f0b3505b45d5713f4498f279c1bc728d1775eba802264b44f0f6cd0db9f', // 1710 - $14.99
		monthly_no_trial_resub_iap: '75ad7b06a31b9ac4733af4ddce331da6', // $12.99 reactivation
		annual_ucb: '2e695f6b5a244b24fc2d5bb99981416c', // $45 annual
		annual_resub_ucb: '2e695f6b5a244b24fc2d5bb99981416c', // $45 reactivation
		annual_resub_ucb_gb: '1cabcdd2cbda6bb07c852053ca65e050916617dbf112dd86f3c54c678836206d', // 1827 - $45 annual
		monthly_ucb: 'f4a0a099513e52fd17d5b3e97518f30356004eda68d537aa0faa165fb7eb38d4', // 1712 - $14.99
		monthly_no_trial_resub_ucb: 'b3adcdb80453082703928d7f6b429a11', // $12.99 reactivation
		annual_upgrade_ucb: 'bd6eaf157d9983d60dd704dedcebe709', // $45 annual
		annual_ucb_gb: '3762ee0a70d541717d098f89a239e25de45e5eb19a5c1475d9c65966936dc105', // 1827 - $45 annual
		monthly_ucb_gb: '411a269086d94ce3646b6ac656018946a6bd7e308db1f929727b7ef718b4862b', // 1821 - $14.99
		monthly_no_trial_resub_ucb_gb:
			'92871caf0af5d09191b46d6b220ade4078c82a7676cfea52dfe11cff3d43d27f', // 1828 - $12.99 reactivation
		semi_annual_upgrade_ucb: '6c1dd228cea87c7ea5c3092da5064be7', // $25.00
	},
};

export const AUXILIARY_PRODUCT_TO_ROLE = {
	assessment: 'abcmouse_assessment_user',
};

export const COOKIES = {
	CONFIG: {
		secure: SECURE,
		domain: DOMAIN,
		expires: 365,
	},
	CJ_CONFIG: {
		secure: SECURE,
		domain: DOMAIN,
		expires: 35,
	},
	DEVICE_ID: 'device_id',
	get DEVICE_ID_CONFIG() {
		return {
			secure: SECURE,
			expires: 399, // days
			path: '/',
			domain: IS_LOCAL_DEVELOPMENT ? '' : '.abcmouse.com',
			sameSite: /** @type {'lax'} */ ('lax'),
		};
	},
	get DEVICE_ID_SSR_CONFIG() {
		// SSR config inherits from DEVICE_ID_CONFIG but excludes 'expires' (js-cookie format)
		// and uses 'maxAge' (SvelteKit format) instead
		const { expires, ...baseConfig } = this.DEVICE_ID_CONFIG;
		return {
			...baseConfig,
			secure: !IS_LOCAL_DEVELOPMENT,
			maxAge: expires * 24 * 60 * 60, // Convert days to seconds
			httpOnly: false,
		};
	},
	CJ_COOKIE: 'cje',
	TRACK_COOKIE: 'track',
	ASSESSMENT_LOGIN: 'assessmentLogin',
	GDPR: 'gdpr',
	EXPERIMENTS: 'x-aofl-experiment-info',
	LEGACY_CAMPAIGN_ID: 's_cid',
	LEGACY_CAMPAIGN_ID_TWO: 's_cid2',
	LEGACY_CAMPAIGN_ID_THREE: 'original_s_cid', // It's stupid, I know (AD). It's okay; at least it's not called dim_52, progress (PG). Perpetual migration (AD).
	LEGACY_CAMPAIGN_VARS: 's_cid_vars',
	UDID: 'udid',
	ADID: 'adid',
	FBC: '_fbc',
	FBP: '_fbp',
	REFERRER: 'referrer',
	RATE_LIMIT_COOKIE: 'abc_forgot_password_rate_limit_until',
	PLOG: 'plog',
};

export const COOKIES_CONSENT = {
	ANALYTICS: 'ANALYTICS_PERFORMANCE_COOKIES',
	TARGETING: 'TARGETING_COOKIES',
};

// TODO: merge this, no reason to have a separate list.
/** @type {{[key:string]: string; }} */
export const COUNTRY_CODES_GDPR = {
	AT: 'Austria',
	AU: 'Australia',
	BE: 'Belgium',
	BG: 'Bulgaria',
	CY: 'Republic of Cyprus',
	CZ: 'Czech Republic',
	DE: 'Germany',
	DK: 'Denmark',
	EE: 'Estonia',
	ES: 'Spain',
	EU: 'European Union',
	FI: 'Finland',
	FR: 'France',
	GB: 'United Kingdom',
	GR: 'Greece',
	HU: 'Hungary',
	HR: 'Croatia',
	IE: 'Ireland',
	IT: 'Italy',
	LT: 'Lithuania',
	LU: 'Luxembourg',
	LV: 'Latvia',
	MT: 'Malta',
	NL: 'Netherlands',
	PL: 'Poland',
	PT: 'Portugal',
	RO: 'Romania',
	SE: 'Sweden',
	SK: 'Slovakia',
	SI: 'Slovenia',
	GDPR: 'GDPR',
};

export const CURRENCY_CODE = {
	AUSTRALIAN_DOLLAR: 'AUD',
	CANADIAN_DOLLAR: 'CAD',
	CHINESE_YUAN: 'CNY',
	EURO: 'EUR',
	BRITISH_POUND: 'GBP',
	ISRAELI_SHEKEL: 'ILS',
	JAPANESE_YEN: 'JPY',
	MEXICAN_PESO: 'MXN',
	NEW_ZEALAND_DOLLAR: 'NZD',
	SWEDISH_KRONA: 'SEK',
	SINGAPORE_DOLLAR: 'SGD',
	TAIWAN_DOLLAR: 'TWD',
	US_DOLLAR: 'USD',
	VIETNAMESE_DONG: 'VND',
};

export const CURRENCY_SYMBOL = {
	AUD: '$',
	CAD: '$',
	CNY: '¥',
	EUR: '€',
	GBP: '£',
	ILS: '₪',
	JPY: '¥',
	MXN: '$',
	NZD: '$',
	SEK: 'kr',
	SGD: '$',
	TWD: 'NT$',
	USD: '$',
	VND: '₫',
};

/**
 * @enum {string}
 */
export const LINK_FROM_APP_PLATFORMS = {
	DEFAULT: 'none',
	IOS: 'ios',
	IOS_1_0: 'ios_1_0',
	IOS_2_0: 'ios_2_0',
	ANDROID: 'android',
	ANDROID_1_0: 'android_1_0',
	ANDROID_2_0: 'android_2_0',
	SAMSUNG: 'samsung',
};

/**
 * @enum {string}
 */
export const LINK_FROM_APP_REFERRERS = {
	APP_1_0: 'abcm1app',
	APP_2_0: 'abcm2app',
};

/**
 * Desktop product selection id (a {@link DESKTOP_CHOICE} value).
 * @typedef {string} DesktopChoice
 */

export const DESKTOP_CHOICE = {
	ABCMOUSE_CLASSIC: 'abcmouse-1',
	ABCMOUSE_2_0: 'abcmouse-2',
};

export const IOS_ALT_PAYMENT_EXPERIMENT_ID_TEST = '10048';

export const LINK_ENTRY_DST_VALUES = {
	STORE_2_0: 'store-2_0',
};

export const OAUTH = {
	get GOOGLE_CLIENT_ID() {
		return returnValueByEnv({
			local: '478715920959-7r9gorbbpk322tbqra9r2b42omoltve5.apps.googleusercontent.com',
			dev: '478715920959-7r9gorbbpk322tbqra9r2b42omoltve5.apps.googleusercontent.com',
			production: '466053052866-nusdc75rleur5jt37rt1l8o4ufn57j28.apps.googleusercontent.com',
		});
	},
	get APPLE_SERVICE_ID() {
		return returnValueByEnv({
			local: 'com.aofl.abcmouse.signin',
			dev: 'com.aofl.abcmouse.signin',
			production: 'com.aofl.abcmouse.signin',
		});
	},
};

export const APIErrorCode = {
	INVALID_CREDENTIALS: 995007,
	CREDENTIALS_ALREADY_IN_USE: 992312,
	INVALID_EMAIL_SYNTAX: 992110,
	GENERAL_ERROR: 992500,
	MAGIC_LINK_REQUEST_REACHED_MAX_ATTEMPTS: 992422,
	MAGIC_LINK_ALREADY_REDEEMED: 992424,
	ACCOUNT_ALREADY_REACTIVATED: 992425,
	OAUTH_ACCOUNT_ALREADY_CONNECTED: 148480,
	PREPAID_CARD_BLOCKED: 992323,
};

/**
 * Gen 4 ONN SUW WebView UA: `100xxxxxx` SKU + `; wv)` in the same parenthetical.
 * Gen 5+ is excluded (ODM `Build.MODEL` not documented; false negatives preferred
 * over false positives). Gen 5 uses `ONN_SOURCE_TAG_PATTERN`. Consumers use
 * `app.meta.isOnnDevice` only.
 */
export const ONN_DEVICE_USER_AGENT_PATTERN = /Android \d+; 100\d{6}\b[^)]*; wv\)/;

/**
 * Gen 5 ONN SUW `src_tag` (`aff:wlmrt:SUW:`). Excludes `vurl`/`qr`/`aff:wm:onn`
 * (non-ONN browser traffic). Gen 4 uses `ONN_DEVICE_USER_AGENT_PATTERN`. Consumers
 * use `app.meta.isOnnDevice` only.
 */
export const ONN_SOURCE_TAG_PATTERN = /^aff:wlmrt:SUW:/;
